/**
 * Pooled 2D particle engine — the physics and rendering are a VERBATIM port
 * of the reference implementation in
 * docs/briefs/REFERENCE-blg-matchday-prototype-v2.html (~L563-588):
 *   - additive blend ('lighter') during render, restored after
 *   - per-frame physics: x+=vx; y+=vy; vy += rising-dot ? 0.005 : 0.06;
 *     life-=dec; rot+=vr
 *   - shapes: dot (arc), streak (2.6× horizontal bar), rect (rotating card)
 *
 * Differences from the prototype (required by the JUICE contract):
 *   - fixed-capacity POOL: zero allocation per frame after warm-up
 *   - hard caps enforced at spawn (60 ceremony / 20 ambient; halved in TRIM;
 *     ≤15 in LITE — see service.particleCap)
 *   - injectable ctx + rng for headless tests.
 */

/**
 * Imperative surface of a mounted ParticleField (see ParticleField.tsx).
 * Defined here (React-free) so the ceremony engine can depend on the type
 * without touching the component layer.
 */
export interface ParticleFieldHandle {
  burst(): void;
  confetti(): void;
  puff(): void;
  ring(cx?: number, cy?: number, col?: string): void;
  embersStart(): void;
  embersStop(): void;
  spawn(count: number, factory: (i: number) => ParticleInit): void;
  clear(): void;
  readonly engine: ParticleEngine | null;
}

/** Minimal Canvas2D surface used by render() — injectable for tests. */
export interface Canvas2DLike {
  globalCompositeOperation: string;
  globalAlpha: number;
  fillStyle: string | CanvasGradient | CanvasPattern;
  clearRect(x: number, y: number, w: number, h: number): void;
  fillRect(x: number, y: number, w: number, h: number): void;
  beginPath(): void;
  arc(x: number, y: number, r: number, a0: number, a1: number): void;
  fill(): void;
  save(): void;
  restore(): void;
  translate(x: number, y: number): void;
  rotate(rad: number): void;
}

export type ParticleShape = "dot" | "streak" | "rect";

export interface ParticleInit {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  dec: number;
  size: number;
  /** "r,g,b" string — tinted at emit time (one white texture serves all). */
  col: string;
  sh: ParticleShape;
  rot?: number;
  vr?: number;
  /** Ember-style buoyancy: rising dots get gravity 0.005 instead of 0.06. */
  rise?: boolean;
}

interface Particle {
  alive: boolean;
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  dec: number;
  size: number;
  col: string;
  sh: ParticleShape;
  rot: number;
  vr: number;
  rise: boolean;
}

function blankParticle(): Particle {
  return {
    alive: false,
    x: 0,
    y: 0,
    vx: 0,
    vy: 0,
    life: 0,
    dec: 1,
    size: 0,
    col: "255,255,255",
    sh: "dot",
    rot: 0,
    vr: 0,
    rise: false,
  };
}

export class ParticleEngine {
  readonly capacity: number;
  /** Fixed pool — identity never changes after construction (test hook). */
  readonly pool: readonly Particle[];
  private readonly freeStack: Int32Array;
  private freeTop: number;
  private aliveN = 0;

  constructor(capacity: number) {
    this.capacity = Math.max(0, Math.floor(capacity));
    const pool: Particle[] = new Array(this.capacity);
    this.freeStack = new Int32Array(this.capacity);
    for (let i = 0; i < this.capacity; i++) {
      pool[i] = blankParticle();
      this.freeStack[i] = this.capacity - 1 - i;
    }
    this.pool = pool;
    this.freeTop = this.capacity;
  }

  get aliveCount(): number {
    return this.aliveN;
  }

  /**
   * Spawn up to n particles from the factory. Requests beyond the cap are
   * DROPPED (the cap is a hard budget, not a queue). Returns spawned count.
   */
  spawn(n: number, factory: (i: number) => ParticleInit): number {
    let spawned = 0;
    for (let i = 0; i < n; i++) {
      if (this.freeTop === 0) break; // pool exhausted — cap enforced
      const idx = this.freeStack[--this.freeTop] ?? 0;
      const p = this.pool[idx];
      if (!p) break;
      const init = factory(i);
      p.alive = true;
      p.x = init.x;
      p.y = init.y;
      p.vx = init.vx;
      p.vy = init.vy;
      p.life = init.life;
      p.dec = init.dec;
      p.size = init.size;
      p.col = init.col;
      p.sh = init.sh;
      p.rot = init.rot ?? 0;
      p.vr = init.vr ?? 0;
      p.rise = init.rise ?? false;
      spawned++;
      this.aliveN++;
    }
    return spawned;
  }

  /** One 60fps frame of the prototype's physics. Zero allocation. */
  step(): void {
    for (let i = 0; i < this.pool.length; i++) {
      const p = this.pool[i];
      if (!p || !p.alive) continue;
      p.x += p.vx;
      p.y += p.vy;
      p.vy += p.sh === "dot" && p.rise && p.vy < 0 ? 0.005 : 0.06;
      p.life -= p.dec;
      if (p.vr) p.rot += p.vr;
      if (p.life <= 0) {
        p.alive = false;
        this.aliveN--;
        this.freeStack[this.freeTop++] = i;
      }
    }
  }

  /** Prototype-verbatim additive render pass. */
  render(ctx: Canvas2DLike): void {
    ctx.globalCompositeOperation = "lighter";
    for (let i = 0; i < this.pool.length; i++) {
      const p = this.pool[i];
      if (!p || !p.alive) continue;
      ctx.globalAlpha = Math.max(0, p.life);
      ctx.fillStyle = `rgb(${p.col})`;
      if (p.sh === "rect") {
        ctx.save();
        ctx.translate(p.x, p.y);
        ctx.rotate(p.rot);
        ctx.fillRect(-p.size / 2, -p.size / 4, p.size, p.size / 2);
        ctx.restore();
      } else if (p.sh === "streak") {
        ctx.fillRect(p.x, p.y, p.size * 2.6, 1.6);
      } else {
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size / 2, 0, 7);
        ctx.fill();
      }
    }
    ctx.globalAlpha = 1;
    ctx.globalCompositeOperation = "source-over";
  }

  /** Kill everything (screen change / ceremony cancel). */
  clear(): void {
    this.freeTop = 0;
    for (let i = 0; i < this.pool.length; i++) {
      const p = this.pool[i];
      if (p) p.alive = false;
      this.freeStack[this.freeTop++] = i;
    }
    this.aliveN = 0;
  }
}
